/**
 * Role create form.
 * POSTs general / appearance / permissions, then writes interface bindings.
 */
import * as React from 'react';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { RoleGeneralSection } from './role-form-builder/RoleGeneralSection';
import { RoleAppearanceSection } from './role-form-builder/RoleAppearanceSection';
import { RolePermissionsSection } from './role-form-builder/RolePermissionsSection';
import {
  applyRoleFormField,
  buildCreateRolePayload,
  buildRoleGroupOptions,
  convertRulesToLegacyBindings,
  emptyRoleForm,
  isCreateRoleValid,
} from './role-form-utils';
import type { RoleFormState } from '@/types/role.types';
import { useCreateRole, useRoleGroups, useReplaceRoleBindings } from '@/hooks/useRoles';
import type { InterfaceElement } from '@/types/interface-elements.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link CreateRoleView}. */
interface CreateRoleViewProps {
  onClose: () => void;
  onSuccess: () => void;
}

/** Role create form. */
export const CreateRoleView: React.FC<CreateRoleViewProps> = ({ onClose, onSuccess }) => {
  const { mutateAsync, isPending } = useCreateRole();
  const { mutateAsync: replaceBindings } = useReplaceRoleBindings();
  const { data: roleGroups = [] } = useRoleGroups();

  const [interfaceElements, setInterfaceElements] = React.useState<
    Record<string, InterfaceElement[]>
  >({});
  const [form, setForm] = React.useState<RoleFormState>(emptyRoleForm);

  const handleFieldChange = (field: keyof RoleFormState, value: unknown) => {
    setForm((prev) => applyRoleFormField(prev, field, value));
  };

  const groupOptions = React.useMemo(() => buildRoleGroupOptions(roleGroups), [roleGroups]);
  const isValid = isCreateRoleValid(form);

  /** Validate, POST the role, then close. */
  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      const created = await mutateAsync(buildCreateRolePayload(form));

      if (Object.keys(form.bindings).length > 0 && Object.keys(interfaceElements).length > 0) {
        const legacyBindings = convertRulesToLegacyBindings(form.bindings, interfaceElements);
        if (legacyBindings.length > 0) {
          await replaceBindings({ roleId: created.id, bindings: legacyBindings });
        }
      }

      toast.success('Rôle créé avec succès !');
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <CollapseReplaceForm
      formType="create"
      title="Créer un rôle"
      subtitle="Renseignez les informations du rôle"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={isPending}
      isValid={isValid}
    >
      <RoleGeneralSection
        form={form}
        onChange={handleFieldChange}
        groupOptions={groupOptions}
        showCode
      />
      <RoleAppearanceSection form={form} onChange={handleFieldChange} />
      <RolePermissionsSection
        form={form}
        onBindingsChange={(bindings) => handleFieldChange('bindings', bindings)}
        onElementsLoaded={setInterfaceElements}
        disabled={isPending}
      />
    </CollapseReplaceForm>
  );
};
